import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import {
  Col,
  DatePicker,
  Icon,
  Input,
  Modal,
  notification,
  Row,
  Select,
  Spin,
  Table,
} from "antd";
import ButtonStandard from "../../../../../../components/utils/button";
import { ContainerConciliation } from "./styles";

import { Formik } from "formik";
import NumberFormat from "react-number-format";
import { useConciliationContext } from "../../../../../../hooks/useConciliationContext";
import { saveConciliation } from "../../../../../../services/conciliationTransactionService";
import { numberMask } from "../../../../../../services/helpersMethodsService";
import SelectFinancialInstallmentsModal from "./components/selectFinancialInstallmentsModal";

// import { Container } from './styles';

// const MOCK_DATA_LIST = [
//   {
//     id: "70cdacd9-be0e-494c-8afd-596f67bedd12",
//     statementTransactionDate: "2023-10-01",
//     statementTransactionAmount: 1000,
//     statementTransactionDescription: "Payment for invoice #123",
//     statementTransactionType: "CREDIT",
//     bovexoFinancialTransactionsInstallments: [
//       {
//         financialTransactionMainInstallmentId:
//           "0495f42b-5bea-4bc7-88d8-655783fc7376",
//         billingDate: "2023-10-01",
//         value: 1000,
//         documentNumber: "Payment for invoice #123",
//       },
//       {
//         financialTransactionMainInstallmentId:
//           "72933607-5167-4f11-9eb2-3f3f2c36d5b4",
//         billingDate: "2023-10-01",
//         value: 1000,
//         documentNumber: "Payment for invoice #123",
//       },
//     ],
//   },
//   {
//     id: "0d1d7096-838b-468a-a555-88a009be51f8",
//     statementTransactionDate: "2023-10-01",
//     statementTransactionAmount: 1000,
//     statementTransactionDescription: "Payment for invoice #123",
//     statementTransactionType: "CREDIT",
//     bovexoFinancialTransactionsInstallments: [
//       {
//         financialTransactionMainInstallmentId:
//           "2d996da5-3fd5-4fcf-a9bb-ce86087e2a40",
//         billingDate: "2023-10-01",
//         value: 1000,
//         documentNumber: "Payment for invoice #123",
//       },
//     ],
//   },
// ];

const ConciliationLayout = () => {
  const [formFilter, setFormFilter] = useState(null);
  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);
  const [isLoadingRequest, setIsLoadingRequest] = useState(false);

  const {
    selectedRowsKeys,
    setSelectedRowKeys,
    selectedRows,
    setSelectedRows,
    dataList,
    isLoadingDataList,
    tableProperties,
    fetchData,
    handleTableChange,
    showModalSelectFinancialInstallments,
    removeFinancialTransactionInstallment,
  } = useConciliationContext();

  const dataListFilted = useMemo(() => {
    if (!formFilter) return dataList;
    return dataList.filter((item) => {
      // Tipo
      if (
        formFilter.statementTransactionType &&
        item.statementTransactionType !== formFilter.statementTransactionType
      ) {
        return false;
      }

      // Data Inicial
      if (
        formFilter.statementTransactionDateFrom &&
        moment(item.statementTransactionDate).isBefore(
          moment(formFilter.statementTransactionDateFrom),
          "day"
        )
      ) {
        return false;
      }

      // Data Final
      if (
        formFilter.statementTransactionDateTo &&
        moment(item.statementTransactionDate).isAfter(
          moment(formFilter.statementTransactionDateTo),
          "day"
        )
      ) {
        return false;
      }

      // Valor mínimo
      if (
        formFilter.statementTransactionAmountMin &&
        Math.abs(Number(item.statementTransactionAmount)) <
          formFilter.statementTransactionAmountMin
      ) {
        return false;
      }

      // Valor máximo
      if (
        formFilter.statementTransactionAmountMax &&
        Math.abs(Number(item.statementTransactionAmount)) >
          formFilter.statementTransactionAmountMax
      ) {
        return false;
      }

      // Descrição
      if (
        formFilter.statementTransactionDescription &&
        !item.statementTransactionDescription
          .toLowerCase()
          .includes(formFilter.statementTransactionDescription.toLowerCase())
      ) {
        return false;
      }

      // Fonte Pagadora
      if (
        formFilter.statementTransactionBankAccountFarmName &&
        !item.statementTransactionBankAccountFarmName
          .toLowerCase()
          .includes(
            formFilter.statementTransactionBankAccountFarmName.toLowerCase()
          )
      ) {
        return false;
      }

      return true;
    });
  }, [dataList, formFilter]);

  const executeSave = useCallback(
    async (data) => {
      setIsLoadingRequest(true);
      try {
        await saveConciliation({
          groupId,
          farmId,
          data,
        });
        notification.success({
          message: translation.conciliation.messages.successConciliation,
        });
        setIsLoadingRequest(false);
        fetchData();
      } catch (error) {
        notification.error({
          message: translation.conciliation.messages.errorConciliation,
        });
        setIsLoadingRequest(false);
        throw error;
      }
    },
    [
      farmId,
      fetchData,
      groupId,
      translation.conciliation.messages.errorConciliation,
      translation.conciliation.messages.successConciliation,
    ]
  );

  const genTableRowClassName = useCallback(
    (record) =>
      ["DEBIT", "PAYMENT", "DIRECTDEBIT"].includes(
        record.statementTransactionType
      )
        ? "red"
        : record.statementTransactionType === "CREDIT"
        ? "green"
        : "",
    []
  );

  const handleSubmitConciliation = useCallback(async () => {
    // Handle the submission of the conciliation
    try {
      // Check if any rows are selected
      if (!selectedRowsKeys || selectedRowsKeys.length === 0) {
        notification.error({
          message:
            translation.conciliation.messages
              .errorNoTransactionsSelectedMessage,
          description:
            translation.conciliation.messages
              .errorNoTransactionsSelectedDescription,
        });
        return;
      }
      // Get the selected records based on the selected keys
      const records = dataList.filter((record) =>
        selectedRowsKeys.includes(record.id)
      );

      // Check if all selected rows have non-empty bovexoFinancialTransactionsInstallments
      const invalidRows = records.filter(
        (row) =>
          !row.bovexoFinancialTransactionsInstallments ||
          row.bovexoFinancialTransactionsInstallments.length === 0
      );

      if (invalidRows.length > 0) {
        // Show error modal with list of invalid statements
        Modal.error({
          width: 600,
          centered: true,
          title:
            translation.conciliation.messages
              .errorSelectedStatementsWithoutTransactionsSelected,
          content: (
            <div>
              <ul>
                {invalidRows.map((row) => (
                  <li key={row.id}>
                    {`${moment(row.statementTransactionDate).format(
                      "DD/MM/YYYY"
                    )} -
                    ${row.statementTransactionDescription}`}
                  </li>
                ))}
              </ul>
            </div>
          ),
        });
        return;
      }

      // Check if all selected rows have all installments with value and sum matches statementTransactionAmount
      const invalidValueRows = records.some((statement) => {
        const installments =
          statement.bovexoFinancialTransactionsInstallments || [];
        // Check if any installment is missing value or not a number
        const hasInvalidValue = installments.some(
          (inst) => typeof inst.value !== "number" || isNaN(inst.value)
        );
        // Check if sum of values matches statementTransactionAmount
        const sumFinancialTransactionsSelected = installments.reduce(
          (acc, row) => {
            const valueTransaction =
              row.transactionType === "income" ? 1 * row.value : -1 * row.value;
            return acc + (valueTransaction || 0);
          },
          0
        );

        const sumMismatch =
          Number(Math.abs(sumFinancialTransactionsSelected).toFixed(2)) !==
          Number(Math.abs(statement.statementTransactionAmount).toFixed(2));
        return hasInvalidValue || sumMismatch;
      });

      if (invalidValueRows) {
        notification.error({
          message:
            translation.conciliation.messages
              .errorTransactionsWithWrongSumValueMessage,
          description:
            translation.conciliation.messages
              .errorTransactionsWithWrongSumValueDescription,
        });
        return;
      }

      // Create the payload for conciliation submission
      const conciliationPayload = {
        transactions: records.map((record) => ({
          conciliationTransactionId: record.id,
          financialTransactionsInstallmentsIds: [
            ...new Set(
              (record.bovexoFinancialTransactionsInstallments || []).map(
                (item) => item.financialTransactionMainInstallmentId
              )
            ),
          ],
        })),
      };
      executeSave(conciliationPayload);
    } catch (error) {
    } finally {
    }
  }, [
    dataList,
    executeSave,
    selectedRowsKeys,
    translation.conciliation.messages.errorNoTransactionsSelectedDescription,
    translation.conciliation.messages.errorNoTransactionsSelectedMessage,
    translation.conciliation.messages
      .errorSelectedStatementsWithoutTransactionsSelected,
    translation.conciliation.messages
      .errorTransactionsWithWrongSumValueDescription,
    translation.conciliation.messages.errorTransactionsWithWrongSumValueMessage,
  ]);

  const handleSubmitSingleConciliation = useCallback(
    async (statement) => {
      // Handle the submission of a single conciliation
      try {
        if (
          !statement.bovexoFinancialTransactionsInstallments ||
          statement.bovexoFinancialTransactionsInstallments.length === 0
        ) {
          // Show error modal with list of invalid statements
          Modal.error({
            width: 600,
            centered: true,
            title:
              "Este lançamento não possui movimentação financeira selecionadas.",
          });
          return;
        }
        // Check if all selected rows have all installments with value and sum matches statementTransactionAmount
        const installments =
          statement.bovexoFinancialTransactionsInstallments || [];
        // Check if any installment is missing value or not a number
        const hasInvalidValue = installments.some(
          (inst) => typeof inst.value !== "number" || isNaN(inst.value)
        );
        // Check if sum of values matches statementTransactionAmount
        const sumFinancialTransactionsSelected = installments.reduce(
          (acc, row) => {
            const valueTransaction =
              row.transactionType === "income" ? 1 * row.value : -1 * row.value;
            return acc + (valueTransaction || 0);
          },
          0
        );

        const sumMismatch =
          Number(Math.abs(sumFinancialTransactionsSelected).toFixed(2)) !==
          Number(Math.abs(statement.statementTransactionAmount).toFixed(2));

        const invalidValueRows = hasInvalidValue || sumMismatch;

        if (invalidValueRows) {
          notification.error({
            message:
              translation.conciliation.messages
                .errorTransactionsWithWrongSumValueMessage,
            description:
              translation.conciliation.messages
                .errorTransactionsWithWrongSumValueDescription,
          });
          return;
        }

        // Create the payload for conciliation submission
        const conciliationPayload = {
          transactions: [
            {
              conciliationTransactionId: statement.id,
              financialTransactionsInstallmentsIds: [
                ...new Set(
                  (statement.bovexoFinancialTransactionsInstallments || []).map(
                    (item) => item.financialTransactionMainInstallmentId
                  )
                ),
              ],
            },
          ],
        };
        executeSave(conciliationPayload);
      } catch (error) {
        console.error("Error during single conciliation:", error);
      } finally {
      }
    },
    [
      executeSave,
      translation.conciliation.messages
        .errorTransactionsWithWrongSumValueDescription,
      translation.conciliation.messages
        .errorTransactionsWithWrongSumValueMessage,
    ]
  );

  useEffect(() => {
    let isMounted = false;
    if (!isMounted) {
      fetchData();
      isMounted = true;
    }
    return () => {
      isMounted = false;
    };
  }, [fetchData]);

  return (
    <ContainerConciliation>
      <Spin spinning={isLoadingRequest} size="large">
        <Formik
          initialValues={{
            statementTransactionType: "",
            statementTransactionDateFrom: null,
            statementTransactionDateTo: null,
            statementTransactionAmountMin: "",
            statementTransactionAmountMax: "",
            statementTransactionDescription: "",
          }}
          onSubmit={(values, { setSubmitting }) => {
            setFormFilter(values);
            setSubmitting(false);
          }}
          onReset={(values, { resetForm }) => {
            setFormFilter(null);
            resetForm({
              statementTransactionType: "",
              statementTransactionDateFrom: null,
              statementTransactionDateTo: null,
              statementTransactionAmountMin: "",
              statementTransactionAmountMax: "",
              statementTransactionDescription: "",
            });
          }}
        >
          {({
            values,
            handleChange,
            handleBlur,
            handleSubmit,
            handleReset,
            setFieldValue,
            isSubmitting,
          }) => (
            <form onSubmit={handleSubmit}>
              <Row
                gutter={16}
                type="flex"
                justify="space-between"
                align="middle"
              >
                <Col xs={24} sm={24} md={20}>
                  <Row gutter={8} type="flex" style={{ marginBottom: 16 }}>
                    <Col xs={24} sm={24} md={6}>
                      <label>
                        {translation.conciliation.form.statementTransactionType}
                      </label>
                      <Select
                        allowClear
                        name="statementTransactionType"
                        value={values.statementTransactionType || undefined}
                        placeholder={translation.defaultSelectPlaceholder}
                        onChange={(value) =>
                          setFieldValue("statementTransactionType", value)
                        }
                        style={{ width: "100%" }}
                      >
                        {Object.entries(translation.statement.types).map(
                          ([key, label]) => (
                            <Select.Option key={key} value={key}>
                              {label}
                            </Select.Option>
                          )
                        )}
                      </Select>
                    </Col>
                    <Col xs={24} sm={24} md={6}>
                      <label>
                        {translation.conciliation.form.statementTransactionDate}
                      </label>
                      <Row gutter={8}>
                        <Col span={12}>
                          <DatePicker
                            name="statementTransactionDateFrom"
                            value={
                              values.statementTransactionDateFrom
                                ? moment(values.statementTransactionDateFrom)
                                : null
                            }
                            disabledDate={(current) =>
                              moment().isBefore(current)
                            }
                            onChange={(date, dateString) =>
                              setFieldValue(
                                "statementTransactionDateFrom",
                                date
                              )
                            }
                            placeholder="Data Inicial"
                            style={{ width: "100%" }}
                            format="DD/MM/YYYY"
                          />
                        </Col>
                        <Col span={12}>
                          <DatePicker
                            name="statementTransactionDateTo"
                            value={
                              values.statementTransactionDateTo
                                ? moment(values.statementTransactionDateTo)
                                : null
                            }
                            disabledDate={(current) =>
                              moment().isBefore(current)
                            }
                            onChange={(date, dateString) =>
                              setFieldValue("statementTransactionDateTo", date)
                            }
                            placeholder="Data Final"
                            style={{ width: "100%" }}
                            format="DD/MM/YYYY"
                          />
                        </Col>
                      </Row>
                    </Col>
                    <Col xs={24} sm={24} md={6}>
                      <label>
                        {
                          translation.conciliation.form
                            .statementTransactionAmount
                        }
                      </label>
                      <Row gutter={8}>
                        <Col span={12}>
                          <NumberFormat
                            name="statementTransactionAmountMin"
                            value={values.statementTransactionAmountMin}
                            onValueChange={({ floatValue }) =>
                              setFieldValue(
                                "statementTransactionAmountMin",
                                floatValue
                              )
                            }
                            customInput={Input}
                            allowLeadingZeros
                            decimalScale={2}
                            thousandSeparator="."
                            decimalSeparator=","
                            allowNegative={false}
                            fixedDecimalScale={true}
                            placeholder="Valor mínimo"
                          />
                        </Col>
                        <Col span={12}>
                          <NumberFormat
                            name="statementTransactionAmountMax"
                            value={values.statementTransactionAmountMax}
                            onValueChange={({ floatValue }) =>
                              setFieldValue(
                                "statementTransactionAmountMax",
                                floatValue
                              )
                            }
                            customInput={Input}
                            allowLeadingZeros
                            decimalScale={2}
                            thousandSeparator="."
                            decimalSeparator=","
                            allowNegative={false}
                            fixedDecimalScale={true}
                            placeholder="Valor máximo"
                          />
                        </Col>
                      </Row>
                    </Col>
                    <Col xs={24} sm={24} md={6}>
                      <label>
                        {
                          translation.conciliation.form
                            .statementTransactionDescription
                        }
                      </label>
                      <Input
                        name="statementTransactionDescription"
                        value={values.statementTransactionDescription}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Descrição"
                      />
                    </Col>
                  </Row>
                  <Row gutter={8} type="flex" style={{ marginBottom: 16 }}>
                    <Col xs={24} sm={24} md={6}>
                      <label>
                        {
                          translation.conciliation.form
                            .statementTransactionBankAccountFarmName
                        }
                      </label>
                      <Input
                        name="statementTransactionBankAccountFarmName"
                        value={values.statementTransactionBankAccountFarmName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Descrição"
                      />
                    </Col>
                  </Row>
                </Col>
                <Col xs={24} sm={24} md={4}>
                  <Row gutter={8} type="flex" justify="end">
                    <Col>
                      <ButtonStandard
                        htmlType="submit"
                        buttonType="type6"
                        loading={isSubmitting}
                      >
                        Filtrar
                      </ButtonStandard>
                    </Col>
                    <Col>
                      <ButtonStandard buttonType="type7" onClick={handleReset}>
                        Limpar
                      </ButtonStandard>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </form>
          )}
        </Formik>
        <Row type="flex" justify="end" gutter={8}>
          <Col>
            <ButtonStandard
              buttonType="typeWithoutBackground"
              onClick={() => fetchData()}
            >
              <Icon type="reload" />
            </ButtonStandard>
          </Col>
          <Col>
            <ButtonStandard
              buttonType={"type6"}
              size={"lg"}
              onClick={handleSubmitConciliation}
            >
              <Icon type="check" /> Conciliar Selecionados
            </ButtonStandard>
          </Col>
        </Row>
        <Row type="flex" style={{ marginTop: 16 }}>
          <Col xs={24} sm={24} md={24} lg={24} xl={24}>
            <Table
              className="tableCustom"
              rowKey={"id"}
              bordered
              loading={isLoadingDataList}
              dataSource={dataListFilted || []}
              rowClassName={genTableRowClassName}
              scroll={{
                x: 1200,
              }}
              pagination={{
                total: dataListFilted != null ? dataListFilted.length : 0,
                size: dataListFilted != null ? tableProperties.size : 0,
                current: dataListFilted != null ? tableProperties.page : 0,
                hideOnSinglePage:
                  dataListFilted !== null &&
                  Object.entries(dataListFilted).length !== 0
                    ? dataListFilted.length > 10
                      ? false
                      : true
                    : true,
                showSizeChanger: true,
                pageSizeOptions: [
                  "10",
                  "20",
                  "30",
                  "100",
                  "500",
                  "1000",
                  "2000",
                ],
              }}
              columns={[
                {
                  title: () => <h2>Extrato</h2>,
                  align: "center",
                  children: [
                    {
                      title:
                        translation.conciliation.table.columns
                          .statementTransactionType,
                      width: 120,
                      dataIndex: "statementTransactionType",
                      key: "statementTransactionType",
                      render: (text) => translation.statement.types[text],
                    },
                    {
                      title:
                        translation.conciliation.table.columns
                          .statementTransactionBankAccountFarmName,
                      width: 180,
                      dataIndex: "statementTransactionBankAccountFarmName",
                      key: "statementTransactionBankAccountFarmName",
                    },
                    {
                      title:
                        translation.conciliation.table.columns
                          .statementTransactionDate,
                      width: 110,
                      dataIndex: "statementTransactionDate",
                      key: "statementTransactionDate",
                      render: (text) => moment(text).format("DD/MM/YYYY"),
                    },
                    {
                      title:
                        translation.conciliation.table.columns
                          .statementTransactionAmount,
                      width: 120,
                      dataIndex: "statementTransactionAmount",
                      key: "statementTransactionAmount",
                      render: (text) => numberMask(text, true),
                    },
                    {
                      title:
                        translation.conciliation.table.columns
                          .statementTransactionDescription,
                      width: 200,
                      dataIndex: "statementTransactionDescription",
                      key: "statementTransactionDescription",
                    },
                  ],
                },
                {
                  align: "center",
                  width: 200,
                  render: (text, record) => {
                    const totalCurrentPageData =
                      record.bovexoFinancialTransactionsInstallments.reduce(
                        (acc, row) => {
                          const valueTransaction =
                            row.transactionType === "income"
                              ? 1 * row.value
                              : -1 * row.value;
                          return acc + (valueTransaction || 0);
                        },
                        0
                      );
                    const differenceBetweenMainValueAndTotalPageData =
                      Math.abs(Number(totalCurrentPageData)).toFixed(2) -
                      Math.abs(
                        Number(record.statementTransactionAmount)
                      ).toFixed(2);
                    return (
                      <>
                        {Math.abs(
                          differenceBetweenMainValueAndTotalPageData
                        ) === 0 && (
                          <Row type="flex" justify="center" gutter={8}>
                            <Col>
                              <ButtonStandard
                                buttonType={"secondary"}
                                onClick={() =>
                                  handleSubmitSingleConciliation(record)
                                }
                              >
                                CONCILIAR
                              </ButtonStandard>
                            </Col>
                          </Row>
                        )}
                        {differenceBetweenMainValueAndTotalPageData !== 0 && (
                          <Row type="flex" justify="center" gutter={8}>
                            <Col>
                              <ButtonStandard
                                buttonType={"secondary"}
                                onClick={() => {
                                  showModalSelectFinancialInstallments(record);
                                }}
                              >
                                SELECIONAR MOV. FINANCEIRA
                              </ButtonStandard>
                            </Col>
                          </Row>
                        )}
                      </>
                    );
                  },
                },
                {
                  title: () => <h2>BovExo</h2>,
                  align: "center",
                  render: (text, mainRecord) => (
                    <Table
                      rowKey={"financialTransactionMainInstallmentId"}
                      dataSource={
                        mainRecord.bovexoFinancialTransactionsInstallments || []
                      }
                      pagination={false}
                      scroll={{
                        x: 1200,
                      }}
                      columns={[
                        {
                          title:
                            translation.conciliation.table.columns
                              .expirationDate,
                          width: 110,
                          dataIndex: "expirationDate",
                          key: "expirationDate",
                          render: (text) => moment(text).format("DD/MM/YYYY"),
                        },
                        {
                          title: translation.conciliation.table.columns.value,
                          width: 120,
                          dataIndex: "value",
                          key: "value",
                          render: (text) => numberMask(text, true),
                        },
                        {
                          title:
                            translation.conciliation.table.columns
                              .supplierClientName,
                          width: 200,
                          dataIndex: "supplierClientName",
                          key: "supplierClientName",
                        },
                        {
                          title:
                            translation.conciliation.table.columns
                              .documentNumber,
                          width: 200,
                          dataIndex: "documentNumber",
                          key: "documentNumber",
                        },
                        {
                          title:
                            translation.conciliation.table.columns
                              .installmentNumber,
                          width: 100,
                          dataIndex: "installmentNumber",
                          key: "installmentNumber",
                          render: (text, record) =>
                            record.installmentNumber +
                            "/" +
                            record.installmentTotal,
                        },
                        {
                          width: 80,
                          render: (text, record) => {
                            return (
                              <Row type="flex" justify="center" gutter={8}>
                                <Col>
                                  <ButtonStandard
                                    buttonType={"cancel"}
                                    onClick={() =>
                                      removeFinancialTransactionInstallment(
                                        mainRecord.id,
                                        record.financialTransactionMainInstallmentId
                                      )
                                    }
                                  >
                                    <Icon type="delete" />
                                  </ButtonStandard>
                                </Col>
                              </Row>
                            );
                          },
                        },
                      ]}
                      footer={(curruentPageData) => {
                        const totalCurrentPageData = Math.abs(
                          curruentPageData.reduce((acc, row) => {
                            const valueTransaction =
                              row.transactionType === "income"
                                ? 1 * row.value
                                : -1 * row.value;
                            return acc + (valueTransaction || 0);
                          }, 0)
                        );
                        const differenceBetweenMainValueAndTotalPageData =
                          Number(totalCurrentPageData).toFixed(2) -
                          Math.abs(
                            Number(mainRecord.statementTransactionAmount)
                          ).toFixed(2);
                        return (
                          <Row type="flex" justify="end" gutter={8}>
                            <Col>
                              <Row>
                                Total:{" "}
                                <strong>
                                  {numberMask(totalCurrentPageData, true)}
                                </strong>
                              </Row>
                              <Row>
                                Diferença:{" "}
                                <strong
                                  className={
                                    differenceBetweenMainValueAndTotalPageData <
                                    0
                                      ? "red"
                                      : "green"
                                  }
                                >
                                  {numberMask(
                                    differenceBetweenMainValueAndTotalPageData,
                                    true
                                  )}
                                </strong>
                              </Row>
                            </Col>
                          </Row>
                        );
                      }}
                    />
                  ),
                },
              ]}
              rowSelection={{
                selectedRowKeys: selectedRowsKeys,
                onSelect: (record, selected, selectedRows) => {
                  if (selected) {
                    setSelectedRowKeys((old) => [...old, record.id]);
                    setSelectedRows((old) => [...old, record]);
                  } else {
                    setSelectedRowKeys((old) =>
                      old.filter((r) => r !== record.id)
                    );
                    setSelectedRows((old) =>
                      old.filter((r) => r.id !== record.id)
                    );
                  }
                },
                onSelectAll: (selected, _, selectedRowsParm) => {
                  // Get just the ids
                  const keys = selectedRowsParm.map((sr) => sr.id);
                  // Get all the selectedAnimalsKeys unless the unselected
                  const lastSelectedKeys = selectedRowsKeys.filter(
                    (sAK) => !keys.includes(sAK)
                  );
                  const lastSelectedRows = selectedRows.filter(
                    (sAK) => !keys.includes(sAK.id)
                  );
                  if (selected) {
                    setSelectedRowKeys((old) => [...old, ...keys]);
                    setSelectedRows((old) => [...old, ...selectedRowsParm]);
                  } else {
                    setSelectedRowKeys(lastSelectedKeys);
                    setSelectedRows(lastSelectedRows);
                  }
                },
              }}
              onChange={handleTableChange}
            />
          </Col>
        </Row>
      </Spin>
    </ContainerConciliation>
  );
};

function Conciliation() {
  return (
    <>
      <ConciliationLayout />
      <SelectFinancialInstallmentsModal />
    </>
  );
}

export default Conciliation;
